import { useState } from "react";

/**
 * First-run welcome tour: a one-time, 3-slide intro shown to a brand-new account
 * before it meets the dashboard. It orients the user (what Broadside does, that
 * they stay in control, that each platform gets a native version) and drops them
 * into connecting their first channel. The parent gates it to first sign-in and
 * remembers dismissal (localStorage), so it never nags. The persistent
 * activation checklist (Onboarding) still lives on the dashboard afterwards.
 */
const SLIDES = [
  {
    emoji: "📡",
    title: "Post once. Reach everywhere.",
    body: "Broadside turns a single upload into a native post on YouTube, TikTok, Instagram, Facebook, and X — so you stop re-uploading the same clip five times.",
  },
  {
    emoji: "🔗",
    title: "Connect the channels you use",
    body: "Link the platforms you actually publish to. You stay fully in control — nothing ever goes out without you reviewing and hitting publish.",
  },
  {
    emoji: "✍️",
    title: "Tailored for each platform",
    body: "Write one idea. Broadside rewrites the hook, caption, and title to fit each platform — and you review every version before it publishes. Schedule it or send it now.",
  },
] as const;

export function WelcomeTour({
  firstName,
  onGetStarted,
  onDismiss,
}: {
  firstName?: string;
  /** Kick off the flow — the parent sends the user to Connections. */
  onGetStarted: () => void;
  /** Close the tour (also called after Get started). Persists "seen". */
  onDismiss: () => void;
}) {
  const [i, setI] = useState(0);
  const last = i === SLIDES.length - 1;
  const slide = SLIDES[i]!;

  return (
    <div className="tour-backdrop" onClick={onDismiss}>
      <div
        className="tour card"
        role="dialog"
        aria-modal="true"
        aria-label="Welcome to Broadside"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="tour-emoji" aria-hidden="true">
          {slide.emoji}
        </div>
        <h2 className="tour-title">
          {i === 0 && firstName ? `Welcome, ${firstName} 👋` : slide.title}
        </h2>
        {i === 0 && firstName ? <p className="tour-sub">{slide.title}</p> : null}
        <p className="tour-body">{slide.body}</p>

        <div className="tour-dots" aria-hidden="true">
          {SLIDES.map((_, n) => (
            <span key={n} className={`tour-dot${n === i ? " on" : ""}`} />
          ))}
        </div>

        <div className="tour-actions">
          {last ? (
            <button
              type="button"
              className="btn primary"
              onClick={() => {
                onGetStarted();
                onDismiss();
              }}
            >
              Connect your first channel →
            </button>
          ) : (
            <button type="button" className="btn primary" onClick={() => setI(i + 1)}>
              Next
            </button>
          )}
          <button type="button" className="btn ghost small tour-skip" onClick={onDismiss}>
            {last ? "Maybe later" : "Skip"}
          </button>
        </div>
      </div>
    </div>
  );
}
